// src/components/gen-application/ApplicationPDF.js
import React, { memo } from "react";
import { StyleSheet } from "@react-pdf/renderer"; // regular import

// Lazy-loaded components
import { Document, Page, Text, View, Image } from "@react-pdf/renderer";
import { sanitizePdfData, sanitizePdfText } from "../utils/pdfSanitize";

const buildBulletList = (items) => {
  const normalized = items
    .map((item) => sanitizePdfText(item).trim())
    .filter(Boolean);

  if (normalized.length === 0) {
    return "N/A";
  }

  return normalized.map((item) => `• ${item}`).join("\n");
};

const isSelectedAffiliation = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const displayPdfValue = (value, fallback = "N/A") => {
  const normalized = sanitizePdfText(value).trim();
  return normalized || fallback;
};

const formatPdfDate = (value) => {
  const normalized = sanitizePdfText(value).trim();

  if (!normalized) {
    return "N/A";
  }

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return normalized;
  }

  return parsed.toLocaleDateString();
};

const hasPdfValue = (value) => {
  const normalized = sanitizePdfText(value).trim().toUpperCase();
  return Boolean(normalized && normalized !== "N/A" && normalized !== "NA");
};

function getBursaryTaxYear(data) {
  // Prefer persisted value from DB if present
  const fromDb = data?.step8?.bursary_tax_year;
  if (fromDb && String(fromDb).trim()) return String(fromDb).trim();

  // Fallback: scholarshipYear - 2 (award year -> tax year)
  const sy = Number(data?.scholarshipYear ?? data?.step9?.scholarship_year);
  if (Number.isFinite(sy) && sy > 1900) return String(sy - 2);

  // Last resort: current year - 2
  return String(new Date().getFullYear() - 2);
}

const styles = StyleSheet.create({
  page: {
    width: "100%",
    maxWidth: "100%",
    margin: 0,
    padding: 25,
    fontSize: 10,
    fontFamily: "Helvetica",
    lineHeight: 1.5,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  headerText: {
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
  },
  logo: {
    width: 250,
    height: 100,
  },
  title: {
    margin: 0,
    padding: 0,
    marginBottom: 10,
    fontSize: 16,
    fontWeight: 900,
  },
  pageHeader: {
    margin: 0,
    padding: 5,
    marginBottom: 20,
  },
  pageHeaderText: {
    margin: 0,
    padding: 0,
    fontSize: 10,
    fontWeight: 900,
  },
  pageHeaderTextName: {
    margin: 0,
    padding: 0,
    fontSize: 14,
    fontWeight: 900,
    marginBottom: 4,
  },
  section: {
    marginBottom: 10,
  },
  sectionHeader: {
    margin: 0,
    marginTop: 10,
    fontSize: 12,
    fontWeight: 900,
    backgroundColor: "#C9C9C9",
    paddingTop: 3,
    paddingRight: 5,
    paddingBottom: 3,
    paddingLeft: 5,
    textAlign: "center",
    borderStyle: "solid",
    borderWidth: 0.75,
    borderColor: "#000",
  },
  table: {
    display: "table",
    width: "100%",
    borderStyle: "solid",
    borderWidth: 0.75,
    borderColor: "#000",
    padding: 0,
    margin: 0,
    marginBottom: 6,
  },
  emptyTableRow: {
    flexDirection: "row",
    margin: 0,
    padding: 2,
  },
  emptyTableRowWithText: {
    flexDirection: "row",
    margin: 0,
    padding: 0,
  },
  infoTableRowFullWidth: {
    flexDirection: "row",
    margin: 0,
    padding: 0,
    borderStyle: "solid",
    borderWidth: 0.75,
    borderColor: "#000",
  },
  tableRow: {
    flexDirection: "row",
    margin: 0,
    padding: 0,
    alignItems: "stretch",
  },
  infoTableRow: {
    flexDirection: "row",
    margin: 0,
    padding: 3,
    borderStyle: "solid",
    borderWidth: 0.75,
    borderColor: "#000",
  },
  tableColHeader: {
    margin: 0,
    padding: 0,
    width: "33.33%",
    minHeight: 12,
    borderStyle: "solid",
    borderWidth: 0.75,
    borderColor: "#000",
    backgroundColor: "#E0E3EB",
    textAlign: "center",
    fontSize: 10,
  },
  tableColHeaderRightAlign: {
    margin: 0,
    padding: 0,
    width: "33.33%",
    minHeight: 12,
    borderStyle: "solid",
    borderWidth: 0.75,
    borderColor: "#000",
    backgroundColor: "#E0E3EB",
    textAlign: "right",
    fontSize: 10,
  },
  tableColHeaderDouble: {
    margin: 0,
    padding: 0,
    width: "66.66%",
    minHeight: 12,
    borderStyle: "solid",
    borderWidth: 0.75,
    borderColor: "#000",
    backgroundColor: "#E0E3EB",
    textAlign: "center",
    fontSize: 10,
  },
  tableColHeaderHalfRowWidth: {
    margin: 0,
    padding: 0,
    width: "50%",
    minHeight: 12,
    borderStyle: "solid",
    borderWidth: 0.75,
    borderColor: "#000",
    backgroundColor: "#E0E3EB",
    textAlign: "center",
    fontSize: 10,
  },
  tableColHeaderHalfRowWidthRightAlign: {
    margin: 0,
    padding: 0,
    width: "50%",
    minHeight: 12,
    borderStyle: "solid",
    borderWidth: 0.75,
    borderColor: "#000",
    backgroundColor: "#E0E3EB",
    textAlign: "right",
    fontSize: 10,
  },
  tableColHeaderFullWidth: {
    margin: 0,
    padding: 0,
    width: "100%",
    minHeight: 12,
    borderStyle: "solid",
    borderWidth: 0.75,
    borderColor: "#000",
    backgroundColor: "#E0E3EB",
    textAlign: "center",
    fontSize: 10,
  },
  tableCol: {
    width: "33.33%",
    borderStyle: "solid",
    borderWidth: 0.75,
    borderColor: "#000",
    margin: 0,
    padding: 0,
  },
  tableColDouble: {
    width: "66.66%",
    borderStyle: "solid",
    borderWidth: 0.75,
    borderColor: "#000",
    margin: 0,
    padding: 0,
  },
  tableColHalfRowWidth: {
    width: "50%",
    borderStyle: "solid",
    borderWidth: 0.75,
    borderColor: "#000",
    margin: 0,
    padding: 0,
  },
  tableColFullWidth: {
    width: "100%",
    margin: 0,
    padding: 0,
  },
  tableCell: {
    width: "100%",
    margin: 0,
    paddingTop: 2,
    paddingRight: 5,
    paddingBottom: 2,
    paddingLeft: 5,
    lineHeight: 1.2,
  },
  tableHeaderCell: {
    width: "100%",
    margin: 0,
    paddingTop: 1,
    paddingRight: 5,
    paddingBottom: 1,
    paddingLeft: 5,
    lineHeight: 1,
    fontWeight: 700,
    color: "#000",
  },
  infoTableCell: {
    width: "100%",
    margin: 0,
    padding: 0,
    marginBottom: 4,
    lineHeight: 1.2,
  },
  infoTableCellItalic: {
    width: "100%",
    margin: 0,
    padding: 0,
    marginBottom: 4,
    fontStyle: "italic",
    lineHeight: 1.2,
  },
  tableCellOfEmptyRow: {
    margin: 0,
    padding: 0,
    fontSize: 6,
    lineHeight: 1.2,
  },
  tableCellOfInfoRow: {
    margin: 0,
    padding: 3,
    textAlign: "left",
    fontWeight: 900,
    fontSize: 8,
    lineHeight: 1.2,
  },
  tableCellForComments: {
    width: "100%",
    height: 150,
    margin: 0,
    padding: 35,
    lineHeight: 1.3,
  },
  tableCellForInfo: {
    width: "100%",
    height: 50,
    margin: 0,
    padding: 15,
  },
  tableCellForInfoFullHeight: {
    width: "100%",
    margin: 0,
    padding: 3,
    lineHeight: 1.2,
  },
  stackedCellText: {
    width: "100%",
    margin: 0,
    paddingTop: 2,
    paddingRight: 5,
    paddingBottom: 2,
    paddingLeft: 5,
    lineHeight: 1.2,
  },
  listItem: {
    fontSize: 10,
    marginBottom: 2,
  },
});

const ApplicationPDF = ({ data: rawData }) => {
  const data = sanitizePdfData(rawData || {});
  const filteredAffiliations = (data.step7 || []).filter((aff) =>
    isSelectedAffiliation(aff.selected)
  );
  const subjectArea =
    data.step2?.Field_of_Study === "--Other--"
      ? data.step2?.Field_of_Study_Other
      : data.step2?.Field_of_Study_Other;
  const hasSchoolGrade10 = [
    data.step4?.school_grade_10_activities,
    data.step4?.school_grade_10_contact_info,
    data.step4?.school_grade_10_hours_served,
  ].some(hasPdfValue);
  const hasSchoolGrade11 = [
    data.step4?.school_grade_11_activities,
    data.step4?.school_grade_11_contact_info,
    data.step4?.school_grade_11_hours_served,
  ].some(hasPdfValue);
  const hasSchoolGrade12 = [
    data.step4?.school_grade_12_activities,
    data.step4?.school_grade_12_contact_info,
    data.step4?.school_grade_12_hours_served,
  ].some(hasPdfValue);
  const hasCommunityGrade10 = [
    data.step5?.community_grade_10_activities,
    data.step5?.community_grade_10_contact_info,
    data.step5?.community_grade_10_hours_served,
  ].some(hasPdfValue);
  const hasCommunityGrade11 = [
    data.step5?.community_grade_11_activities,
    data.step5?.community_grade_11_contact_info,
    data.step5?.community_grade_11_hours_served,
  ].some(hasPdfValue);
  const hasCommunityGrade12 = [
    data.step5?.community_grade_12_activities,
    data.step5?.community_grade_12_contact_info,
    data.step5?.community_grade_12_hours_served,
  ].some(hasPdfValue);
  const appIDandStatus = `${data.step9?.id || ""} (${data.step9?.application_status || ""})`;
  const studentFullName = `${data.step1?.first_name || ""} ${data.step1?.middle_name || ""} ${data.step1?.last_name || ""}`;
  const generatedDate = sanitizePdfText(new Date().toLocaleString());
  const studentAddress = `${data.step1?.address || ""}, ${data.step1?.city || ""}, ${data.step1?.province || ""}, ${data.step1?.postalCode || ""}`;
  const PS_VIU = data.step2?.PS_VIU ? "- Vancouver Island University" : null;
  const PS_UBC = data.step2?.PS_UBC ? "- University of BC" : null;
  const PS_UNBC = data.step2?.PS_UNBC ? "- UNBC" : null;
  const PS_UVIC = data.step2?.PS_UVIC ? "- University of Victoria" : null;
  const PS_SFU = data.step2?.PS_SFU ? "- Simon Fraser University" : null;
  const PS_Other = data.step2?.PS_Other || null;
  const school_activity_sports = data.step4?.school_activity_sports
    ? "- Sport Activities"
    : null;
  const school_activity_school_volunteer = data.step4
    .school_activity_school_volunteer
    ? "- School Volunteer"
    : null;
  const school_activity_school_clubs = data.step4.school_activity_school_clubs
    ? "- School Clubs"
    : null;
  const school_activity_students_council = data.step4
    .school_activity_students_council
    ? "- Students Council"
    : null;
  const postSecondaryAppliedTo = buildBulletList([
    PS_VIU,
    PS_UBC,
    PS_UNBC,
    PS_UVIC,
    PS_SFU,
    PS_Other,
  ]);
  const schoolActivitiesSelected = buildBulletList([
    school_activity_sports,
    school_activity_school_volunteer,
    school_activity_school_clubs,
    school_activity_students_council,
  ]);
  function formatYMDToMDY(ymdString) {
    if (!ymdString) return "";
    const [year, month, day] = ymdString.split("-");
    return `${parseInt(month, 10)}/${parseInt(day, 10)}/${year}`;
  }

  const bursaryTaxYear = getBursaryTaxYear(data);

  return (
    <Document>
      {/* First Page */}
      <Page size="LETTER" orientation="portrait" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerText}>
            <Text style={styles.title}>Scholarship Application</Text>
            {/* Page Header */}
            <View style={styles.pageHeader}>
              <Text style={styles.pageHeaderTextName}>
                {`${data.step1.first_name} ${data.step1.last_name}`}
              </Text>
              <Text style={styles.pageHeaderText}>{data.step4.school}</Text>
              <Text style={styles.pageHeaderText}>
                Application ID: {appIDandStatus}
              </Text>
              <Text style={styles.pageHeaderText}>
                Generated: {generatedDate}
              </Text>
            </View>
          </View>
          <Image
            src="https://sas.nlsf.ca/nlsf-scholarships.png"
            style={styles.logo}
          />
        </View>

        {/* Step 1: Personal Information */}
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>Personal Information</Text>
          <View style={styles.table}>
            {/* Table Header */}
            <View style={styles.tableRow}>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableHeaderCell}>Full Name</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableHeaderCell}>Gender</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableHeaderCell}>SIN</Text>
              </View>
            </View>
            {/* Table Row */}
            <View style={styles.tableRow}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                  {displayPdfValue(studentFullName)}
                </Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                  {displayPdfValue(data.step1.gender)}
                </Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                  {displayPdfValue(data.step1.SIN)}
                </Text>
              </View>
            </View>
            {/* Empty Row */}
            <View style={styles.emptyTableRow}></View>

            {/* Additional Rows */}
            <View style={styles.tableRow}>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableHeaderCell}>Citizenship</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableHeaderCell}>School</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableHeaderCell}>CTC?</Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                  {displayPdfValue(data.step1.citizenship)}
                </Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                  {displayPdfValue(data.step4.school)}
                </Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                  {isSelectedAffiliation(data.step1.CTC) ? "Yes" : "No"}
                </Text>
              </View>
            </View>
            {/* Empty Row */}
            <View style={styles.emptyTableRow}></View>

            {/* Date of Birth */}
            <View style={styles.tableRow}>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableHeaderCell}>Address</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableHeaderCell}>PEN</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableHeaderCell}>Date of Birth</Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                  {displayPdfValue(studentAddress)}
                </Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                  {displayPdfValue(data.step1.PEN)}
                </Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                  {displayPdfValue(formatYMDToMDY(data.step1.dob))}
                </Text>
              </View>
            </View>
            {/* Empty Row */}
            <View style={styles.emptyTableRow}></View>

            <View style={styles.tableRow}>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableHeaderCell}>Cell Phone</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableHeaderCell}>Home Phone</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableHeaderCell}>Work Phone</Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                  {displayPdfValue(data.step1.cellPhone)}
                </Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                  {displayPdfValue(data.step1.homePhone)}
                </Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                  {displayPdfValue(data.step1.workPhone)}
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Step 2: Career and Post-Secondary Information */}
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>
            Career and Post-Secondary Information
          </Text>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableHeaderCell}>Career/Job Direction</Text>
              </View>
              <View style={styles.tableColHeaderDouble}>
                <Text style={styles.tableHeaderCell}>Direct to Post-Secondary</Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                  {displayPdfValue(data.step2.career_job_direction)}
                </Text>
              </View>
              <View style={styles.tableColDouble}>
                <Text style={styles.tableCell}>
                  {isSelectedAffiliation(data.step2.direct_to_post_secondary)
                    ? "Yes"
                    : "No"}
                </Text>
              </View>
            </View>
            {/* Empty Row */}
            <View style={styles.emptyTableRow}></View>

            <View style={styles.tableRow}>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableHeaderCell}>Program Type</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableHeaderCell}>Field of Study</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableHeaderCell}>Subject Area</Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                  {displayPdfValue(data.step2.PS_Path)}
                </Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                  {displayPdfValue(data.step2.Field_of_Study)}
                </Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                  {displayPdfValue(subjectArea)}
                </Text>
              </View>
            </View>
            {/* Empty Row */}
            <View style={styles.emptyTableRow}></View>

            <View style={styles.tableRow}>
              <View style={styles.tableColHeaderDouble}>
                <Text style={styles.tableHeaderCell}>Post Secondary Applied To</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableHeaderCell}>Enrolling in (Program)</Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableColDouble}>
                <Text style={styles.stackedCellText}>
                  {displayPdfValue(postSecondaryAppliedTo)}
                </Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                  {displayPdfValue(data.step2.PS_Program_Name)}
                </Text>
              </View>
            </View>
            {/* Empty Row */}
            <View style={styles.emptyTableRow}></View>

            <View style={styles.tableRow}>
              <View style={styles.tableColHeaderFullWidth}>
                <Text style={styles.tableHeaderCell}>
                  Been Accepted to Post-Secondary Institution
                </Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableColFullWidth}>
                <Text style={styles.tableCell}>
                  {isSelectedAffiliation(data.step2.beenAccepted)
                    ? "Yes"
                    : "No"}
                </Text>
              </View>
            </View>
            {/* Empty Row */}
            <View style={styles.emptyTableRow}></View>

            <View style={styles.tableRow}>
              <View style={styles.tableColHeaderFullWidth}>
                <Text style={styles.tableHeaderCell}>Educational Plans</Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableColFullWidth}>
                <Text style={styles.tableCell}>
                  {displayPdfValue(data.step2.other_education_plans)}
                </Text>
              </View>
            </View>
            {/* Empty Row */}
            <View style={styles.emptyTableRow}></View>

            <View style={styles.tableRow}>
              <View style={styles.tableColHeaderFullWidth}>
                <Text style={styles.tableHeaderCell}>
                  INFORMATION ON THIS APPLICATION IS CERTIFIED CORRECT
                </Text>
              </View>
            </View>
            <View style={styles.emptyTableRowWithText}>
              <Text style={styles.tableCellOfEmptyRow}>
                By dating and typing your name you certify that all the
                infomation you have provided is accurate and correct to the best
                of your knowledge.
              </Text>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableHeaderCell}>
                  Certification Date
                </Text>
              </View>
              <View style={styles.tableColHeaderDouble}>
                <Text style={styles.tableHeaderCell}>
                  Typed Signature
                </Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                  {formatPdfDate(data.step2.academic_signed_date)}
                </Text>
              </View>
              <View style={styles.tableColDouble}>
                <Text style={styles.tableCell}>
                  {displayPdfValue(data.step2.academic_signed_name)}
                </Text>
              </View>
            </View>
            {/* Empty Row */}
          </View>
        </View>
      </Page>
      {/* Second Page */}
      <Page size="LETTER" orientation="portrait" style={styles.page}>
        {/* Page Header */}
        <View style={styles.pageHeader}>
          <Text style={styles.pageHeaderTextName}>
            {`${data.step1.first_name} ${data.step1.last_name}`}
          </Text>
          <Text style={styles.pageHeaderText}>{data.step4.school}</Text>
          <Text style={styles.pageHeaderText}>
            Application ID: {appIDandStatus}
          </Text>
          <Text style={styles.pageHeaderText}>Generated: {generatedDate}</Text>
        </View>

        {/* Selected Subject Based Awards */}
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>
            Selected Subject Based Awards
          </Text>
          <View style={styles.table}>
            {/* Table Header */}
            <View style={styles.tableRow}>
              <View style={styles.tableColHeaderHalfRowWidth}>
                <Text style={styles.tableHeaderCell}>Subject</Text>
              </View>
              <View style={styles.tableColHeaderHalfRowWidth}>
                <Text style={styles.tableHeaderCell}>More Info</Text>
              </View>
            </View>
            {/* Table Rows */}
            {data.step3 && data.step3.length > 0 ? (
              data.step3.map((award, index) => (
                <View style={styles.tableRow} key={index}>
                  <View style={styles.tableColHalfRowWidth}>
                    <Text style={styles.tableCell}>{award.subject}</Text>
                  </View>
                  <View style={styles.tableColHalfRowWidth}>
                    <Text style={styles.tableCell}>{award.moreinfo || ""}</Text>
                  </View>
                </View>
              ))
            ) : (
              <View style={styles.tableRow}>
                <View style={styles.tableCol} />
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>No Awards Selected</Text>
                </View>
              </View>
            )}
          </View>
        </View>

        {/* Selected Affiliations */}
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>Selected Affiliations</Text>
          {/* Empty Row */}
          <View style={styles.infoTableRowFullWidth}>
            <Text style={styles.tableCellOfInfoRow}>
              This section contains awards with special affiliations and
              criteria to be met. READ the descriptions in the booklet and IF
              YOU MEET THE CRITERIA for any of these awards, please tick and
              fill in the information required and you will be prompted to fill
              in additional info for some selections.
            </Text>
          </View>
          <View style={styles.table}>
            {/* Table Header */}
            <View style={styles.tableRow}>
              <View style={styles.tableColHeaderHalfRowWidth}>
                <Text style={styles.tableHeaderCell}>Affiliation</Text>
              </View>
              <View style={styles.tableColHeaderHalfRowWidth}>
                <Text style={styles.tableHeaderCell}>More Info</Text>
              </View>
            </View>
            {/* Table Rows */}
            {filteredAffiliations && filteredAffiliations.length > 0 ? (
              filteredAffiliations.map((aff, index) => (
                <View style={styles.tableRow} key={index}>
                  <View style={styles.tableColHalfRowWidth}>
                    <Text style={styles.tableCell}>{aff.Affiliation}</Text>
                  </View>
                  <View style={styles.tableColHalfRowWidth}>
                    <Text style={styles.tableCell}>
                      {displayPdfValue(aff.moreinfo)}
                    </Text>
                  </View>
                </View>
              ))
            ) : (
              <View style={styles.tableRow}>
                <View style={styles.tableCol} />
                <View style={styles.tableCol} />
                <View style={styles.tableCol} />
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>No Affiliations</Text>
                </View>
              </View>
            )}
          </View>
        </View>
      </Page>
      {/* Third Page */}
      <Page size="LETTER" orientation="portrait" style={styles.page}>
        {/* Page Header */}
        <View style={styles.pageHeader}>
          <Text style={styles.pageHeaderTextName}>
            {`${data.step1.first_name} ${data.step1.last_name}`}
          </Text>
          <Text style={styles.pageHeaderText}>{data.step4.school}</Text>
          <Text style={styles.pageHeaderText}>
            Application ID: {appIDandStatus}
          </Text>
          <Text style={styles.pageHeaderText}>Generated: {generatedDate}</Text>
        </View>

        {/* School Activities Documentation */}
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>
            School Activities Documentation
          </Text>
          {/* Info Row */}
          <View style={styles.infoTableRowFullWidth}>
            <Text style={styles.tableCellOfInfoRow}>
              School Activities Documentation - Include your activities over the
              last three years – Grades 10, 11 and 12. Please note: Within the
              “hours served” box please note which of these categories applies
              to this activity: 10 – 50 hours, 50 – 100 hours, 100 – 200 hours,
              or over 200 hours.
            </Text>
          </View>
          <View style={styles.table}>
            {/* Table Headers */}
            <View style={styles.tableRow}>
              <View style={styles.tableColHeaderFullWidth}>
                <Text style={styles.tableHeaderCell}>School Activities Selected</Text>
              </View>
            </View>
            {/* Table Rows */}
            <View style={styles.tableRow}>
              <View style={styles.tableColFullWidth}>
                <Text style={styles.stackedCellText}>
                  {schoolActivitiesSelected}
                </Text>
              </View>
            </View>
            <View style={styles.infoTableRowFullWidth}>
              <Text style={styles.tableCellOfInfoRow}>
                List below, in point form, all activities in which you
                participated during each grade, including the capacity in which
                you were involved and time commitment. Include any leadership
                positions you held and achievements you have made in areas such
                as citizenship, sports, the arts, music groups, clubs, teams,
                etc. You may be required to verify your participation in the
                activities you have listed.
              </Text>
            </View>
            {/* Grade-wise Activities */}
            {hasSchoolGrade10 && (
              <>
                <Text style={styles.sectionHeader}>Grade 10</Text>
                <View style={styles.table}>
                  <View style={styles.tableRow}>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableHeaderCell}>Activities</Text>
                    </View>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableHeaderCell}>
                        Contact Name/Phone #
                      </Text>
                    </View>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableHeaderCell}>Hours Served</Text>
                    </View>
                  </View>
                  <View style={styles.tableRow}>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        {displayPdfValue(
                          data.step4.school_grade_10_activities,
                          ""
                        )}
                      </Text>
                    </View>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        {displayPdfValue(
                          data.step4.school_grade_10_contact_info,
                          ""
                        )}
                      </Text>
                    </View>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        {displayPdfValue(
                          data.step4.school_grade_10_hours_served,
                          ""
                        )}
                      </Text>
                    </View>
                  </View>
                </View>
              </>
            )}
            {hasSchoolGrade11 && (
              <>
                <Text style={styles.sectionHeader}>Grade 11</Text>
                <View style={styles.table}>
                  <View style={styles.tableRow}>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableHeaderCell}>Activities</Text>
                    </View>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableHeaderCell}>
                        Contact Name/Phone #
                      </Text>
                    </View>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableHeaderCell}>Hours Served</Text>
                    </View>
                  </View>
                  <View style={styles.tableRow}>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        {displayPdfValue(
                          data.step4.school_grade_11_activities,
                          ""
                        )}
                      </Text>
                    </View>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        {displayPdfValue(
                          data.step4.school_grade_11_contact_info,
                          ""
                        )}
                      </Text>
                    </View>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        {displayPdfValue(
                          data.step4.school_grade_11_hours_served,
                          ""
                        )}
                      </Text>
                    </View>
                  </View>
                </View>
              </>
            )}
            {hasSchoolGrade12 && (
              <>
                <Text style={styles.sectionHeader}>Grade 12</Text>
                <View style={styles.table}>
                  <View style={styles.tableRow}>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableHeaderCell}>Activities</Text>
                    </View>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableHeaderCell}>
                        Contact Name/Phone #
                      </Text>
                    </View>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableHeaderCell}>Hours Served</Text>
                    </View>
                  </View>
                  <View style={styles.tableRow}>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        {displayPdfValue(
                          data.step4.school_grade_12_activities,
                          ""
                        )}
                      </Text>
                    </View>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        {displayPdfValue(
                          data.step4.school_grade_12_contact_info,
                          ""
                        )}
                      </Text>
                    </View>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        {displayPdfValue(
                          data.step4.school_grade_12_hours_served,
                          ""
                        )}
                      </Text>
                    </View>
                  </View>
                </View>
              </>
            )}
          </View>
        </View>
      </Page>
      {/* Fourth Page */}
      <Page size="LETTER" orientation="portrait" style={styles.page}>
        {/* Page Header */}
        <View style={styles.pageHeader}>
          <Text style={styles.pageHeaderTextName}>
            {`${data.step1.first_name} ${data.step1.last_name}`}
          </Text>
          <Text style={styles.pageHeaderText}>{data.step4.school}</Text>
          <Text style={styles.pageHeaderText}>
            Application ID: {appIDandStatus}
          </Text>
          <Text style={styles.pageHeaderText}>Generated: {generatedDate}</Text>
        </View>

        {/* Community Activities Documentation */}
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>
            Community Activities Documentation
          </Text>
          {/* Info Row */}
          <View style={styles.infoTableRow}>
            <View style={styles.tableColFullWidth}>
              <Text style={styles.infoTableCell}>
                Community, Personal, and Volunteer Activities Documentation –
                Include your activities over the last three years – Grades 10,
                11 and 12.{" "}
              </Text>
              <Text style={styles.infoTableCell}>
                Please note: Within the “hours served” box please note which of
                these categories applies to this activity: 10 – 50 hours, 50 –
                100 hours, 100 – 200 hours, or over 200 hours.{" "}
              </Text>
              <Text style={styles.infoTableCell}>
                List below, in point form, all activities in which you
                participated during each grade, including the capacity in which
                you were involved and time commitment. Include any leadership
                positions you held and achievements you have made in areas such
                as citizenship, sports, the arts, music groups, clubs, teams,
                etc. You may be required to verify your participation in the
                activities you have listed.
              </Text>
            </View>
          </View>
          <View style={styles.table}>
            {/* Grade-wise Activities */}
            {hasCommunityGrade10 && (
              <>
                <Text style={styles.sectionHeader}>Grade 10</Text>
                <View style={styles.table}>
                  <View style={styles.tableRow}>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableHeaderCell}>Activities</Text>
                    </View>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableHeaderCell}>
                        Contact Name/Phone #
                      </Text>
                    </View>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableHeaderCell}>Hours Served</Text>
                    </View>
                  </View>
                  <View style={styles.tableRow}>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        {displayPdfValue(
                          data.step5.community_grade_10_activities,
                          ""
                        )}
                      </Text>
                    </View>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        {displayPdfValue(
                          data.step5.community_grade_10_contact_info,
                          ""
                        )}
                      </Text>
                    </View>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        {displayPdfValue(
                          data.step5.community_grade_10_hours_served,
                          ""
                        )}
                      </Text>
                    </View>
                  </View>
                </View>
              </>
            )}
            {hasCommunityGrade11 && (
              <>
                <Text style={styles.sectionHeader}>Grade 11</Text>
                <View style={styles.table}>
                  <View style={styles.tableRow}>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableHeaderCell}>Activities</Text>
                    </View>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableHeaderCell}>
                        Contact Name/Phone #
                      </Text>
                    </View>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableHeaderCell}>Hours Served</Text>
                    </View>
                  </View>
                  <View style={styles.tableRow}>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        {displayPdfValue(
                          data.step5.community_grade_11_activities,
                          ""
                        )}
                      </Text>
                    </View>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        {displayPdfValue(
                          data.step5.community_grade_11_contact_info,
                          ""
                        )}
                      </Text>
                    </View>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        {displayPdfValue(
                          data.step5.community_grade_11_hours_served,
                          ""
                        )}
                      </Text>
                    </View>
                  </View>
                </View>
              </>
            )}
            {hasCommunityGrade12 && (
              <>
                <Text style={styles.sectionHeader}>Grade 12</Text>
                <View style={styles.table}>
                  <View style={styles.tableRow}>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableHeaderCell}>Activities</Text>
                    </View>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableHeaderCell}>
                        Contact Name/Phone #
                      </Text>
                    </View>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableHeaderCell}>Hours Served</Text>
                    </View>
                  </View>
                  <View style={styles.tableRow}>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        {displayPdfValue(
                          data.step5.community_grade_12_activities,
                          ""
                        )}
                      </Text>
                    </View>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        {displayPdfValue(
                          data.step5.community_grade_12_contact_info,
                          ""
                        )}
                      </Text>
                    </View>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        {displayPdfValue(
                          data.step5.community_grade_12_hours_served,
                          ""
                        )}
                      </Text>
                    </View>
                  </View>
                </View>
              </>
            )}
          </View>
        </View>
      </Page>
      {/* Fifth Page */}
      <Page size="LETTER" orientation="portrait" style={styles.page}>
        {/* Page Header */}
        <View style={styles.pageHeader}>
          <Text style={styles.pageHeaderTextName}>
            {`${data.step1.first_name} ${data.step1.last_name}`}
          </Text>
          <Text style={styles.pageHeaderText}>{data.step4.school}</Text>
          <Text style={styles.pageHeaderText}>
            Application ID: {appIDandStatus}
          </Text>
          <Text style={styles.pageHeaderText}>Generated: {generatedDate}</Text>
        </View>

        {/* Personal Information */}
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>Personal Information</Text>
          {/* Info Row */}
          <View style={styles.infoTableRow}>
            <View style={styles.tableColFullWidth}>
              <Text style={styles.infoTableCell}>
                Please list in point form any important information that you
                would like the selection committee members to know about you.
                Please emphasize any special circumstances, family situations,
                personal and career plans, qualifications and other details that
                will support your application.
              </Text>
            </View>
          </View>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <View style={styles.tableColHeaderFullWidth}>
                <Text style={styles.tableHeaderCell}>Information</Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableColFullWidth}>
                <Text style={styles.tableCell}>
                  {displayPdfValue(data.step6?.personal_info)}
                </Text>
              </View>
            </View>
          </View>
          <Text style={styles.sectionHeader}>
            Please choose a max of five (5) Awards that you wish to be
            considered for and provide a brief explanation on your Personal
            Information above.
          </Text>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <View style={styles.tableColHeaderFullWidth}>
                <Text style={styles.tableHeaderCell}>Selections</Text>
              </View>
            </View>
            {data.step6?.choice1 && (
              <View style={styles.tableRow}>
                <View style={styles.tableColHeader}>
                  <Text style={styles.tableHeaderCell}>Choice 1</Text>
                </View>
                <View style={styles.tableColDouble}>
                  <Text style={styles.tableCell}>
                    {displayPdfValue(data.step6?.choice1)}
                  </Text>
                </View>
              </View>
            )}
            {data.step6?.choice2 && (
              <View style={styles.tableRow}>
                <View style={styles.tableColHeader}>
                  <Text style={styles.tableHeaderCell}>Choice 2</Text>
                </View>
                <View style={styles.tableColDouble}>
                  <Text style={styles.tableCell}>
                    {displayPdfValue(data.step6?.choice2)}
                  </Text>
                </View>
              </View>
            )}
            {data.step6?.choice3 && (
              <View style={styles.tableRow}>
                <View style={styles.tableColHeader}>
                  <Text style={styles.tableHeaderCell}>Choice 3</Text>
                </View>
                <View style={styles.tableColDouble}>
                  <Text style={styles.tableCell}>
                    {displayPdfValue(data.step6?.choice3)}
                  </Text>
                </View>
              </View>
            )}
            {data.step6?.choice4 && (
              <View style={styles.tableRow}>
                <View style={styles.tableColHeader}>
                  <Text style={styles.tableHeaderCell}>Choice 4</Text>
                </View>
                <View style={styles.tableColDouble}>
                  <Text style={styles.tableCell}>
                    {displayPdfValue(data.step6?.choice4)}
                  </Text>
                </View>
              </View>
            )}
            {data.step6?.choice5 && (
              <View style={styles.tableRow}>
                <View style={styles.tableColHeader}>
                  <Text style={styles.tableHeaderCell}>Choice 5</Text>
                </View>
                <View style={styles.tableColDouble}>
                  <Text style={styles.tableCell}>
                    {displayPdfValue(data.step6?.choice5)}
                  </Text>
                </View>
              </View>
            )}
            {!data.step6?.choice1 &&
              !data.step6?.choice2 &&
              !data.step6?.choice3 &&
              !data.step6?.choice4 &&
              !data.step6?.choice5 && (
                <View style={styles.tableRow}>
                  <View style={styles.tableColFullWidth}>
                    <Text style={styles.tableCell}>No Selections</Text>
                  </View>
                </View>
              )}
          </View>
          <Text style={styles.sectionHeader}>Admin Comments </Text>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <View style={styles.tableColFullWidth}>
                <Text style={styles.tableCellForComments}></Text>
              </View>
            </View>
          </View>
        </View>
      </Page>

      {/* Sixth Page */}
      <Page size="LETTER" orientation="portrait" style={styles.page}>
        {/* Page Header */}
        <View style={styles.pageHeader}>
          <Text style={styles.pageHeaderTextName}>
            {`${data.step1.first_name} ${data.step1.last_name}`}
          </Text>
          <Text style={styles.pageHeaderText}>{data.step4.school}</Text>
          <Text style={styles.pageHeaderText}>
            Application ID: {appIDandStatus}
          </Text>
          <Text style={styles.pageHeaderText}>Generated: {generatedDate}</Text>
        </View>

        {/* Application for Bursaries */}
        <View style={styles.section}>
          <Text style={styles.sectionHeader}>Application for Bursaries</Text>
          <View style={styles.infoTableRow}>
            <View style={styles.tableColFullWidth}>
              <Text style={styles.infoTableCell}>
                PLEASE NOTE: Completion of this section is optional for
                scholarship applicants. However, candidates applying for
                bursaries where criteria include financial need must provide
                this information for the Scholarship Committee. All responses
                will be treated with the strictest confidence.{" "}
              </Text>
              <Text style={styles.infoTableCell}>
                Students who do not complete this form and include it with their
                application will not be considered for bursaries.
              </Text>
            </View>
          </View>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <View style={styles.tableColHeaderFullWidth}>
                <Text style={styles.tableHeaderCell}>Total Family Income</Text>
              </View>
            </View>
            <View style={styles.infoTableRow}>
              <View style={styles.tableColFullWidth}>
                <Text style={styles.infoTableCell}>
                  Completion of all parts of this section is required. If
                  insufficient background information is given, the awards
                  committee may not be able to determine eligibility for a
                  bursary.
                </Text>
              </View>
            </View>

            <View style={styles.tableRow}>
              <View style={styles.tableColHeaderRightAlign}>
                <Text style={styles.tableHeaderCell}>Lives with:</Text>
              </View>
              <View style={styles.tableColDouble}>
                <Text style={styles.tableCell}>{`${
                  data.step8.bursary_resides_with || ""
                }${
                  data.step8.bursary_siblings
                    ? " and " + data.step8.bursary_siblings + " sibling(s)"
                    : ""
                }`}</Text>
              </View>
            </View>
            <View style={styles.infoTableRow}>
              <View style={styles.tableColFullWidth}>
                <Text style={styles.infoTableCell}>Income</Text>
                <Text style={styles.infoTableCell}>
                  (From Line 150 Income Tax Return of {bursaryTaxYear} for each
                  of the following people):
                </Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableColHeaderHalfRowWidthRightAlign}>
                <Text style={styles.tableHeaderCell}>{"Father's (Guardian):"}</Text>
              </View>
              <View style={styles.tableColHalfRowWidth}>
                <Text style={styles.tableCell}>
                  ${data.step8.bursary_gross_income_father}
                </Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableColHeaderHalfRowWidthRightAlign}>
                <Text style={styles.tableHeaderCell}>{"Mother's (Guardian):"}</Text>
              </View>
              <View style={styles.tableColHalfRowWidth}>
                <Text style={styles.tableCell}>
                  ${data.step8.bursary_gross_income_mother}
                </Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableColHeaderHalfRowWidthRightAlign}>
                <Text style={styles.tableHeaderCell}>
                  {"Step-Father's (Guardian):"}
                </Text>
              </View>
              <View style={styles.tableColHalfRowWidth}>
                <Text style={styles.tableCell}>
                  ${data.step8.bursary_gross_income_stepfather}
                </Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableColHeaderHalfRowWidthRightAlign}>
                <Text style={styles.tableHeaderCell}>
                  {"Step-Mother's (Guardian):"}
                </Text>
              </View>
              <View style={styles.tableColHalfRowWidth}>
                <Text style={styles.tableCell}>
                  ${data.step8.bursary_gross_income_stepmother}
                </Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableColHeaderHalfRowWidthRightAlign}>
                <Text style={styles.tableHeaderCell}>Other Income:</Text>
              </View>
              <View style={styles.tableColHalfRowWidth}>
                <Text style={styles.tableCell}>
                  ${data.step8.bursary_gross_income_other}
                </Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableColHeaderHalfRowWidthRightAlign}>
                <Text style={styles.tableHeaderCell}>Source of Other:</Text>
              </View>
              <View style={styles.tableColHalfRowWidth}>
                <Text style={styles.tableCell}>
                  {data.step8.bursary_gross_income_other_explain}
                </Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableColHeaderHalfRowWidthRightAlign}>
                <Text style={styles.tableHeaderCell}>Total Income:</Text>
              </View>
              <View style={styles.tableColHalfRowWidth}>
                <Text style={styles.tableCell}>
                  ${data.step8.bursary_total_income}
                </Text>
              </View>
            </View>
            <View style={styles.emptyTableRowWithText}>
              <Text style={styles.tableCell}>
                Please describe your financial need below. List any determining
                factors that may qualify you for a bursary.
              </Text>
            </View>
            <View style={styles.infoTableRow}>
              <View style={styles.tableColFullWidth}>
                <Text style={styles.tableCellForInfoFullHeight}>
                  {displayPdfValue(data.step8.bursary_reason)}
                </Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableColHeaderHalfRowWidthRightAlign}>
                <Text style={styles.tableHeaderCell}>
                  Number of Siblings at Home:
                </Text>
              </View>
              <View style={styles.tableColHalfRowWidth}>
                <Text style={styles.tableCell}>
                  {data.step8.bursary_siblings}
                </Text>
              </View>
            </View>

            <View style={styles.tableRow}>
              <View style={styles.tableColHeaderFullWidth}>
                <Text style={styles.tableHeaderCell}>
                  INFORMATION ON THIS APPLICATION IS CERTIFIED CORRECT
                </Text>
              </View>
            </View>
            <View style={styles.infoTableRow}>
              <View style={styles.tableColFullWidth}>
                <Text style={styles.infoTableCellItalic}>
                  I hereby declare that all of the above information is true and
                  correct. I authorize the District Awards Committee to verify
                  any or all of the above statements, which may be reviewed by
                  the selection committee or donors as required.
                </Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableHeaderCell}>
                  Certification Date
                </Text>
              </View>
              <View style={styles.tableColHeaderDouble}>
                <Text style={styles.tableHeaderCell}>
                  Typed Signature
                </Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                  {formatPdfDate(data.step8.bursary_signing_date)}
                </Text>
              </View>
              <View style={styles.tableColDouble}>
                <Text style={styles.tableCell}>
                  {displayPdfValue(data.step8.bursary_student_signing)}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default memo(ApplicationPDF);
